/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { useImperativeProgressTracker } from 'src/core/hooks/useImperativeProgressTracker';

type ProgressTrackerOptions<T> = Partial<{
    /**
     * Ensures that the tracker state stays marked as
     * completed when the tracker does complete.
     */
    persistOnComplete: boolean;

    /**
     * A callback to fire when the tracker is marked as
     * completed. Will only fire once per promise resolution.
     */
    onComplete: (data?: T) => void;
}>;

/**
 * This tracker is used to track progress for handler operations
 * in order for the components to be able to react to them.
 */
export const useProgressTracker = <T>(options?: ProgressTrackerOptions<T>) => {
    const { createTracker } = useImperativeProgressTracker();
    const optionsRef = React.useRef<ProgressTrackerOptions<T>>();

    React.useEffect(() => {
        optionsRef.current = options;
    }, [options]);

    const [trackProgressStore] = React.useState(() =>
        createTracker<T>({
            onDone: context => {
                if (trackProgressStore.isCompleted) {
                    if (optionsRef.current?.onComplete) {
                        optionsRef.current.onComplete(context);
                    }

                    if (!optionsRef.current?.persistOnComplete) {
                        trackProgressStore.reset();
                    }
                }
            }
        })
    );

    return trackProgressStore;
};
